// Tip math shared by checkout, the controller and the customer display, so a
// "18%" tap shows the same amount on every screen. Tips are a percentage of
// the sale total (after discounts, fees and tax) and are never taxed - the
// server stores them in sales.tip_amount, outside total_price.

export const roundCents = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const tipFromPercent = (total, percent) =>
  roundCents(Math.max(0, Number(total) || 0) * (Number(percent) || 0) / 100);

// ── Tip pool (Timesheets > Tips) ─────────────────────────────────────────────
// Each day's tips are split among the people who worked that day, by the
// minutes they worked that day (Kelly, 2026-10-10). Days are the browser's
// local calendar days, the same boundaries every sales/shift range query uses.

export const localDayKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

// A completed shift's worked time, minus its finished breaks, as minutes per
// local day ({ 'YYYY-MM-DD': minutes }). Shifts over midnight split across
// both days. Open shifts count nothing until they're clocked out.
export const workedMinutesByDay = (shift) => {
  if (!shift.clockedOutAt) return {};
  let segs = [[new Date(shift.clockedInAt).getTime(), new Date(shift.clockedOutAt).getTime()]];
  for (const b of shift.breaks ?? []) {
    if (!b.endedAt) continue;
    const bs = new Date(b.startedAt).getTime();
    const be = new Date(b.endedAt).getTime();
    segs = segs.flatMap(([s, e]) => {
      if (be <= s || bs >= e) return [[s, e]];
      return [[s, Math.max(s, bs)], [Math.min(e, be), e]].filter(([a, z]) => z > a);
    });
  }
  const out = {};
  for (let [s, e] of segs) {
    while (s < e) {
      const next = new Date(s); next.setHours(24, 0, 0, 0);
      const end = Math.min(e, next.getTime());
      const key = localDayKey(s);
      out[key] = (out[key] ?? 0) + (end - s) / 60000;
      s = end;
    }
  }
  return out;
};

// Split `cents` by weights so the parts always add back up exactly (largest
// remainder: whole cents first, then the leftover cents to the biggest
// fractions).
const splitCents = (cents, weights) => {
  const total = weights.reduce((a, w) => a + w, 0);
  if (total <= 0) return weights.map(() => 0);
  const raw = weights.map((w) => (cents * w) / total);
  const out = raw.map(Math.floor);
  let left = cents - out.reduce((a, n) => a + n, 0);
  raw.map((r, i) => [r - out[i], i]).sort((a, b) => b[0] - a[0] || a[1] - b[1])
    .forEach(([, i]) => { if (left > 0) { out[i]++; left--; } });
  return out;
};

// tips: [{ date, amount }] (one per sale with a tip)
// shifts: [{ staffId, clockedInAt, clockedOutAt, breaks }]
// inPool(shift) -> bool: whether this person shares (owner never does)
// Returns { days, people, totalTips, pooledTips, unassigned, pooledMinutes }:
//   days: [{ day, tips, minutes, shares: [{ staffId, minutes, amount }] }] (newest first)
//   people: [{ staffId, minutes, amount, days }] (biggest share first)
//   unassigned: tips on days nobody in the pool worked (nobody to split with)
export const computeTipPool = ({ tips, shifts, inPool }) => {
  const tipCents = new Map();
  for (const t of tips) {
    const c = Math.round((Number(t.amount) || 0) * 100);
    if (c <= 0) continue;
    const key = localDayKey(t.date);
    tipCents.set(key, (tipCents.get(key) ?? 0) + c);
  }
  const minutes = new Map(); // day -> Map(staffId -> minutes)
  for (const sh of shifts) {
    if (!inPool(sh)) continue;
    for (const [day, m] of Object.entries(workedMinutesByDay(sh))) {
      if (!minutes.has(day)) minutes.set(day, new Map());
      const byStaff = minutes.get(day);
      byStaff.set(sh.staffId, (byStaff.get(sh.staffId) ?? 0) + m);
    }
  }

  const days = [];
  const people = new Map();
  let unassigned = 0;
  for (const [day, cents] of tipCents) {
    const byStaff = [...(minutes.get(day) ?? new Map())].filter(([, m]) => m > 0);
    const dayMinutes = byStaff.reduce((a, [, m]) => a + m, 0);
    if (!byStaff.length) {
      unassigned += cents;
      days.push({ day, tips: cents / 100, minutes: 0, shares: [] });
      continue;
    }
    const parts = splitCents(cents, byStaff.map(([, m]) => m));
    const shares = byStaff.map(([staffId, m], i) => ({ staffId, minutes: m, amount: parts[i] / 100 }));
    for (const sh of shares) {
      const p = people.get(sh.staffId) ?? { staffId: sh.staffId, minutes: 0, cents: 0, days: 0 };
      p.minutes += sh.minutes;
      p.cents += Math.round(sh.amount * 100);
      p.days += 1;
      people.set(sh.staffId, p);
    }
    days.push({ day, tips: cents / 100, minutes: dayMinutes, shares: shares.sort((a, b) => b.amount - a.amount) });
  }
  days.sort((a, b) => (a.day < b.day ? 1 : -1));
  const totalCents = [...tipCents.values()].reduce((a, c) => a + c, 0);
  return {
    days,
    people: [...people.values()]
      .map((p) => ({ staffId: p.staffId, minutes: p.minutes, amount: p.cents / 100, days: p.days }))
      .sort((a, b) => b.amount - a.amount),
    totalTips: totalCents / 100,
    pooledTips: (totalCents - unassigned) / 100,
    unassigned: unassigned / 100,
    pooledMinutes: [...people.values()].reduce((a, p) => a + p.minutes, 0),
  };
};

// tip = { mode: 'none' | 'percent' | 'custom' | 'qr', percent?, amount? }
// 'qr' = the customer tipped with the shop's own Venmo/Cash App QR, which
// Pointbunny can't see, so it records nothing.
export const tipAmountFor = (tip, total) => {
  if (!tip) return 0;
  if (tip.mode === 'percent') return tipFromPercent(total, tip.percent);
  if (tip.mode === 'custom') return roundCents(Math.max(0, Number(tip.amount) || 0));
  return 0;
};
